import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildTranscriptMessageKey,
  resolveClaudiaTranscriptMessageThreadId,
} from "../lib/claudia-transcript-identity";
import type {
  AssistantPromptExecutionRuntime,
  AssistantRuntimeRunSummary,
  AssistantTranscriptMessageRuntime,
  AssistantTranscriptNarration,
  AssistantTranscriptTimelineItem,
  OptimisticTranscriptExchange,
} from "../types";
import { buildOptimisticTranscriptItems } from "./claudia-transcript-optimistic-items";
import type { useClaudiaTranscriptMessages } from "./use-claudia-transcript-messages";

export type ClaudiaTranscriptMessageFeed = ReturnType<typeof useClaudiaTranscriptMessages>;

function compareTimelineItems(
  left: Pick<AssistantTranscriptTimelineItem, "order" | "stepOrder">,
  right: Pick<AssistantTranscriptTimelineItem, "order" | "stepOrder">
) {
  return left.order === right.order ? left.stepOrder - right.stepOrder : left.order - right.order;
}

function isTerminalPromptPhase(phase: string | null | undefined) {
  return phase === "COMPLETED" || phase === "FAILED" || phase === "STOPPED";
}

/**
 * A row from the vendored message stream, shaped like the projected transcript row it will become.
 *
 * `threadId` is passed in rather than read off the row: the caller has already resolved it (a
 * stream row carries it in `metadata`, a settled row on the row itself) and dropped everything
 * that does not belong to the thread on screen (LAT-950).
 */
function toTimelineMessage(
  message: AssistantTranscriptMessageRuntime,
  threadId: string
): Extract<AssistantTranscriptTimelineItem, { kind: "message" }> {
  return {
    ...message,
    kind: "message",
    threadId,
    key:
      message.transcriptItemKey ??
      message.key ??
      `${message.role}:${message.order}:${message.stepOrder}`,
    // A row still streaming has neither settled into a status nor finished its text.
    status: message.status ?? "streaming",
    text: message.text ?? "",
    finality: message.finality ?? "streaming",
  };
}

function getItemKey(item: AssistantTranscriptTimelineItem) {
  const key = item.transcriptItemKey ?? item.key;
  if (!item.transcriptItemKey && import.meta.env.DEV) {
    console.warn("Claudia transcript item is missing transcriptItemKey", {
      kind: item.kind,
      key,
    });
  }
  return key;
}

function getAssistantAttemptKey(item: AssistantTranscriptTimelineItem) {
  if (
    item.kind !== "message" ||
    item.role !== "assistant" ||
    (item.messageKind && item.messageKind !== "prompt_response") ||
    !item.promptExecutionAttemptId
  ) {
    return null;
  }
  return `attempt:${item.promptExecutionAttemptId}`;
}

function isNarrationCandidate(
  item: AssistantTranscriptTimelineItem,
  activePromptMessageId: string
) {
  return (
    item.kind === "message" &&
    item.role === "assistant" &&
    item.finality === "streaming" &&
    item.promptMessageId === activePromptMessageId
  );
}

function isLiveActivityTurn(
  item: AssistantTranscriptTimelineItem
): item is Extract<AssistantTranscriptTimelineItem, { kind: "activity_turn" }> {
  return item.kind === "activity_turn" && item.status === "in_progress";
}

function getTimelineMessageId(item: AssistantTranscriptTimelineItem) {
  if (item.kind !== "message") {
    return null;
  }
  return item._id ?? item.id ?? null;
}

function filterActivityOwnedStreamingMessages(items: AssistantTranscriptTimelineItem[]) {
  // The upstream delta-stream hook can retain assembled client rows after Convex has removed the
  // owned stream from the active stream list. Suppress those ghosts by the transcript key stamped
  // while the stream was live; synthetic stream ids are not stable component-message ids.
  const activityOwnedMessageIds = new Set<string>();
  const activityOwnedTranscriptKeys = new Set<string>();

  for (const item of items) {
    if (item.kind !== "activity_turn") {
      continue;
    }
    for (const activityItem of item.items) {
      if (
        (activityItem.kind !== "preamble" && activityItem.kind !== "assistant_progress") ||
        !activityItem.messageId
      ) {
        continue;
      }
      activityOwnedMessageIds.add(activityItem.messageId);
      activityOwnedTranscriptKeys.add(buildTranscriptMessageKey(activityItem.messageId));
    }
  }

  if (activityOwnedMessageIds.size === 0) {
    return items;
  }

  return items.filter((item) => {
    if (item.kind !== "message" || item.role !== "assistant" || item.finality !== "streaming") {
      return true;
    }
    const messageId = getTimelineMessageId(item);
    if (messageId && activityOwnedMessageIds.has(messageId)) {
      return false;
    }
    return !(item.transcriptItemKey && activityOwnedTranscriptKeys.has(item.transcriptItemKey));
  });
}

function getActiveNarrationPromptMessageId(args: {
  items: AssistantTranscriptTimelineItem[];
  optimisticPromptMessageId?: string | null;
  promptExecutionPhase?: string | null;
}) {
  if (args.optimisticPromptMessageId && !isTerminalPromptPhase(args.promptExecutionPhase ?? null)) {
    return args.optimisticPromptMessageId;
  }

  const liveActivityTurn = [...args.items].reverse().find(isLiveActivityTurn);
  return liveActivityTurn?.promptMessageId ?? null;
}

function splitNarration(args: {
  items: AssistantTranscriptTimelineItem[];
  activePromptMessageId: string | null;
}): { items: AssistantTranscriptTimelineItem[]; narration: AssistantTranscriptNarration | null } {
  const activePromptMessageId = args.activePromptMessageId;
  if (!activePromptMessageId) {
    return { items: args.items, narration: null };
  }

  const candidates = args.items
    .filter((item) => isNarrationCandidate(item, activePromptMessageId))
    .sort(compareTimelineItems);
  const latestCandidate = candidates[candidates.length - 1] ?? null;
  if (!latestCandidate || latestCandidate.kind !== "message") {
    return { items: args.items, narration: null };
  }

  const candidateKeys = new Set(candidates.map((item) => getItemKey(item)));
  return {
    items: args.items.filter((item) => !candidateKeys.has(getItemKey(item))),
    narration: latestCandidate.text?.trim()
      ? {
          key: getItemKey(latestCandidate),
          text: latestCandidate.text,
          promptMessageId: latestCandidate.promptMessageId ?? null,
        }
      : null,
  };
}

function mergeTranscriptItem(args: {
  byKey: Map<string, AssistantTranscriptTimelineItem>;
  keyByAttempt: Map<string, string>;
  item: AssistantTranscriptTimelineItem;
}) {
  const key = getItemKey(args.item);
  const attemptKey = getAssistantAttemptKey(args.item);
  // Transcript rendering currently assumes at most one renderable assistant message per attempt.
  // Progress/commentary rows must be ownership-suppressed before this merge.
  const existingKey = attemptKey ? (args.keyByAttempt.get(attemptKey) ?? null) : null;
  const existing = existingKey ? args.byKey.get(existingKey) : args.byKey.get(key);

  if (existingKey && existingKey !== key) {
    if (existing?.finality === "final" && args.item.finality !== "final") {
      return;
    }
    args.byKey.delete(existingKey);
  } else if (existing?.finality === "final" && args.item.finality !== "final") {
    return;
  }

  args.byKey.set(key, args.item);
  if (attemptKey) {
    args.keyByAttempt.set(attemptKey, key);
  }
}

/**
 * The prompt whose runtime is still writing, or null when the thread is idle.
 *
 * Both Claudia mount points resolve this the same way, and both must resolve it BEFORE the
 * transcript feed: it decides which messages go into the live subscription and which go into
 * the settled one (LAT-880).
 */
export function resolveLivePromptMessageId(args: {
  runtimeRun?: AssistantRuntimeRunSummary | null;
  promptExecutionStatus?: AssistantPromptExecutionRuntime | null;
  /** Newest prompt in the loaded window — the live anchor while the runtime is idle. */
  latestPromptMessageId?: string | null;
}) {
  const execution = args.promptExecutionStatus;
  if (execution?.promptMessageId && !isTerminalPromptPhase(execution.phase)) {
    return execution.promptMessageId;
  }
  const run = args.runtimeRun;
  if (run?.promptMessageId && !isTerminalRunStatus(run.status)) {
    return run.promptMessageId;
  }
  // Idle threads keep the newest prompt live. Its window is one prompt either way, and it is the
  // only window that can still gain queue markers ("not answered", "sent again") after the run
  // that owned them is gone — those come from the live-only thread fallbacks.
  return args.latestPromptMessageId ?? null;
}

function isTerminalRunStatus(status: string | null | undefined) {
  return (
    status === "COMPLETED" ||
    status === "COMPLETED_WITH_WARNINGS" ||
    status === "FAILED" ||
    status === "CANCELLED"
  );
}

/**
 * The thread a timeline item names, for the kinds that name one (messages, activity turns).
 * Structural on purpose: the union has variants with no thread of their own, and those are
 * reachable only through a window that was already scoped to the thread.
 */
function readTimelineItemThreadId(item: object): string | null {
  const threadId = (item as { threadId?: unknown }).threadId;
  return typeof threadId === "string" ? threadId : null;
}

function getMessageStableId(message: Pick<AssistantTranscriptMessageRuntime, "_id" | "id">) {
  return message._id ?? message.id ?? null;
}

const ID_SEPARATOR = "|";

/**
 * Split the loaded message window at the live prompt.
 *
 * Everything before the live prompt is terminal history whose rows can no longer change;
 * everything from the live prompt onwards belongs to the run in flight (including steering
 * messages, which carry their own prompt id but are written by the running prompt).
 */
function partitionTranscriptWindow(args: {
  messages: AssistantTranscriptMessageRuntime[];
  threadId: string | null;
  livePromptMessageId: string | null;
}) {
  // LAT-990: the ids in this window become `messageIds` on both timeline subscriptions, which are
  // asked under `args.threadId`. The feed behind it is the vendored stream cache that survives a
  // thread switch (LAT-950), so an unfiltered window asks this thread for the previous thread's
  // rows — and the server used to answer, stamping them with the thread that was asked for.
  const persisted = args.messages.filter(
    (message) =>
      message.status !== "streaming" &&
      resolveClaudiaTranscriptMessageThreadId(message) === args.threadId
  );
  const ids = persisted
    .map((message) => getMessageStableId(message))
    .filter((id): id is string => Boolean(id));
  if (!args.livePromptMessageId) {
    return { settledIds: ids, liveIds: [] as string[] };
  }
  const liveIndex = persisted.findIndex(
    (message) =>
      getMessageStableId(message) === args.livePromptMessageId ||
      message.promptMessageId === args.livePromptMessageId
  );
  if (liveIndex < 0) {
    // The live prompt has not landed in this page yet (fresh submit, or it lives on an older
    // page). Subscribe to it directly so its markers still render.
    return { settledIds: ids, liveIds: [args.livePromptMessageId] };
  }
  const settled = new Set<string>();
  const live = new Set<string>([args.livePromptMessageId]);
  persisted.forEach((message, index) => {
    const id = getMessageStableId(message);
    if (!id) {
      return;
    }
    (index < liveIndex ? settled : live).add(id);
  });
  return { settledIds: [...settled], liveIds: [...live] };
}

export function useClaudiaTranscriptItems(args: {
  projectId: Id<"projects">;
  threadId: string | null;
  /** Owned by the caller so liveness can be resolved before the timeline subscriptions. */
  messageFeed: ClaudiaTranscriptMessageFeed;
  livePromptMessageId?: string | null;
  optimisticExchange?: OptimisticTranscriptExchange | null;
}) {
  const messageQuery = args.messageFeed;
  const { settledIds, liveIds } = useMemo(
    () =>
      partitionTranscriptWindow({
        messages: messageQuery.results,
        threadId: args.threadId,
        livePromptMessageId: args.livePromptMessageId ?? null,
      }),
    [args.livePromptMessageId, args.threadId, messageQuery.results]
  );
  // Value-stable args: Convex re-subscribes on any argument change, so the id arrays must be
  // both sorted (a reordered window is the same window) and referentially stable across
  // re-renders that did not change the window.
  const settledKey = [...settledIds].sort().join(ID_SEPARATOR);
  const settledMessageIds = useMemo(
    () => (settledKey ? settledKey.split(ID_SEPARATOR) : []),
    [settledKey]
  );

  // No-flicker: when a prompt turns terminal it moves from the live window to the settled one,
  // and the settled subscription returns `undefined` until its new argument set resolves. Keep
  // the transitioning ids in the live window until then; the merge below dedupes by key.
  const [resolvedSettledSignature, setResolvedSettledSignature] = useState<string | null>(null);
  const previousLiveIdsRef = useRef<string[]>([]);
  const retentionThreadIdRef = useRef<string | null>(args.threadId);
  if (retentionThreadIdRef.current !== args.threadId) {
    retentionThreadIdRef.current = args.threadId;
    previousLiveIdsRef.current = [];
  }
  const settledSignature = `${args.threadId ?? ""}::${settledKey}`;
  const settledArgsResolved = resolvedSettledSignature === settledSignature;
  const liveKey = [
    ...new Set([
      ...liveIds,
      ...(settledArgsResolved
        ? []
        : previousLiveIdsRef.current.filter((id) => settledMessageIds.includes(id))),
    ]),
  ]
    .sort()
    .join(ID_SEPARATOR);
  const liveMessageIds = useMemo(() => (liveKey ? liveKey.split(ID_SEPARATOR) : []), [liveKey]);

  const settledTimelineQuery = useQuery(
    api.claudia.listTranscriptTimeline,
    args.threadId
      ? {
          projectId: args.projectId,
          threadId: args.threadId,
          messageIds: settledMessageIds,
          liveWindow: false,
        }
      : "skip"
  );
  const liveTimelineQuery = useQuery(
    api.claudia.listTranscriptTimeline,
    args.threadId && liveMessageIds.length > 0
      ? {
          projectId: args.projectId,
          threadId: args.threadId,
          messageIds: liveMessageIds,
          liveWindow: true,
        }
      : "skip"
  );

  useEffect(() => {
    previousLiveIdsRef.current = liveMessageIds;
  }, [liveMessageIds]);
  useEffect(() => {
    if (settledTimelineQuery !== undefined && resolvedSettledSignature !== settledSignature) {
      setResolvedSettledSignature(settledSignature);
    }
  }, [resolvedSettledSignature, settledSignature, settledTimelineQuery]);

  // Scroll-back grows `settledMessageIds`, which re-subscribes the settled query and makes it
  // `undefined` for one round trip. Without a fallback every activity turn in the transcript would
  // blink out and back on each older page — and the heights moving under the reader is exactly what
  // the scroll anchor is trying to hold still (LAT-952). The retained items describe a SUBSET of the
  // window now being asked for, so keeping them is strictly closer to the truth than dropping them.
  const retainedSettledTimelineRef = useRef<{
    threadId: string;
    items: AssistantTranscriptTimelineItem[];
  } | null>(null);
  const resolvedSettledTimeline =
    settledTimelineQuery?.threadId === args.threadId ? settledTimelineQuery.items : undefined;
  if (resolvedSettledTimeline !== undefined && args.threadId) {
    retainedSettledTimelineRef.current = {
      threadId: args.threadId,
      items: resolvedSettledTimeline,
    };
  }
  const settledTimeline =
    resolvedSettledTimeline ??
    (retainedSettledTimelineRef.current?.threadId === args.threadId
      ? retainedSettledTimelineRef.current.items
      : undefined);
  const liveTimeline =
    liveTimelineQuery?.threadId === args.threadId ? liveTimelineQuery.items : undefined;
  const persistedTimeline = useMemo(
    () =>
      settledTimeline === undefined && liveTimeline === undefined
        ? undefined
        : [...(settledTimeline ?? []), ...(liveTimeline ?? [])],
    [liveTimeline, settledTimeline]
  );
  const promptStatus = useQuery(
    api.claudia.getPromptExecutionStatus,
    args.threadId && args.optimisticExchange?.promptMessageId
      ? {
          projectId: args.projectId,
          threadId: args.threadId,
          promptMessageId: args.optimisticExchange.promptMessageId,
        }
      : "skip"
  );

  const mergedResults = useMemo(() => {
    const byKey = new Map<string, AssistantTranscriptTimelineItem>();
    const keyByAttempt = new Map<string, string>();
    for (const item of persistedTimeline ?? []) {
      // LAT-990: the query envelope only proves the ARGUMENTS named this thread. Items name their
      // own thread now, so an item that came back under this thread's window while belonging to
      // another one is dropped here rather than rendered — the same rule the message feed below
      // has always applied, on the input that used to be exempt from it.
      const itemThreadId = readTimelineItemThreadId(item);
      if (itemThreadId !== null && itemThreadId !== args.threadId) {
        continue;
      }
      mergeTranscriptItem({
        byKey,
        keyByAttempt,
        item: { ...item, finality: item.finality ?? "final" },
      });
    }
    // The message feed is the one merge input that is not thread-gated by its query envelope
    // (both timeline queries are, above). It is fed by a vendored per-component stream cache
    // that survives a thread switch, so a row here can outlive the thread it was written for.
    // Every row the feed can legitimately emit names its thread — settled rows are stamped by
    // `toTranscriptMessage`, stream rows through `metadata.claudiaTranscript.threadId` — so the
    // rule is strict: no resolvable match, no merge (LAT-950). With no thread on screen there is
    // nothing for a row to match, so the feed contributes nothing at all.
    const feedThreadId = args.threadId;
    const liveMessages = !feedThreadId
      ? []
      : messageQuery.results
          .filter((message) => resolveClaudiaTranscriptMessageThreadId(message) === feedThreadId)
          .map((message) => toTimelineMessage(message, feedThreadId))
          .filter((message) => {
            const exchange = args.optimisticExchange;
            return !(
              exchange &&
              message.role === "user" &&
              !message.transcriptItemKey &&
              message.status === "pending" &&
              (message.sourceOrder === exchange.sourceOrder ||
                message.text?.trim() === exchange.prompt.trim())
            );
          });
    for (const message of liveMessages) {
      mergeTranscriptItem({ byKey, keyByAttempt, item: message });
    }
    const canonicalItems = [...byKey.values()];
    const withOptimisticItems = [
      ...canonicalItems,
      ...buildOptimisticTranscriptItems({
        exchange: args.optimisticExchange,
        threadId: args.threadId,
        items: canonicalItems,
        promptExecutionPhase: promptStatus?.phase,
      }),
    ];
    const activityOwnedFilteredItems = filterActivityOwnedStreamingMessages(withOptimisticItems);
    const activePromptMessageId = getActiveNarrationPromptMessageId({
      items: activityOwnedFilteredItems,
      optimisticPromptMessageId: args.optimisticExchange?.promptMessageId ?? null,
      promptExecutionPhase: promptStatus?.phase,
    });
    const { items, narration } = splitNarration({
      items: activityOwnedFilteredItems,
      activePromptMessageId,
    });
    const hasActiveAssistantMessage = Boolean(
      activePromptMessageId &&
        items.some(
          (item) =>
            item.kind === "message" &&
            item.role === "assistant" &&
            item.promptMessageId === activePromptMessageId
        )
    );

    return {
      items: items.sort(compareTimelineItems),
      narration,
      hasActiveAssistantMessage,
    };
  }, [
    args.optimisticExchange,
    args.threadId,
    messageQuery.results,
    persistedTimeline,
    promptStatus?.phase,
  ]);

  const results = mergedResults.items;
  const narration = mergedResults.narration;
  const hasActiveAssistantMessage = mergedResults.hasActiveAssistantMessage;

  const messages = useMemo(
    () =>
      results.filter(
        (item): item is Extract<AssistantTranscriptTimelineItem, { kind: "message" }> =>
          item.kind === "message"
      ),
    [results]
  );

  return {
    loadMore: messageQuery.loadMore,
    // The transcript's own `status` below is a two-state summary for the empty-state check; these
    // are the paginated feed's real signals, and scroll-back needs them (LAT-952).
    canLoadMore: messageQuery.status === "CanLoadMore",
    isLoadingMore: messageQuery.status === "LoadingMore",
    messages,
    decorations: results.filter((item) => item.kind !== "message"),
    results,
    narration,
    hasActiveAssistantMessage,
    // "Exhausted" with zero results asserts the thread is genuinely empty — the full-screen
    // page redirects abandoned sidebar drafts on that signal (LAT-855). The transcript merges
    // two sources, so it is only "loaded" once BOTH the persisted timeline query and the live
    // message stream have delivered their first page; the timeline alone resolving first must
    // not make a thread with messages read as empty.
    status:
      args.threadId &&
      results.length === 0 &&
      (persistedTimeline === undefined || messageQuery.status === "LoadingFirstPage")
        ? ("LoadingFirstPage" as const)
        : ("Exhausted" as const),
  };
}
